// Every project on the Work pages. Media items are either
//   { kind: "youtube", id, poster, caption }  or  { kind: "image", src, poster?, caption }
// (poster = still frame shown until the GIF/video is asked for).
import { ACCENT, BLUE, GOLD, HEAT, MINT, VIOLET } from "../theme";

export const PROJECTS = [
  {
    slug: "dishcare",
    name: "Dishcare",
    color: ACCENT,
    role: "Co-founder",
    when: "2021–23",
    kicker: "An autonomous robotic dishwasher.",
    summary:
      "A custom robot with a specialized gripper and computer vision — 6D pose estimation and dirt detection — for a closed-loop clean that uses half the water and energy of the best dishwashers.",
    cover: "/custom_robot.jpg",
    hover: "/dishcare.gif",
    link: { label: "dish.care", href: "https://dish.care" },
    stats: [
      ["50%", "less water & energy"],
      ["80%", "faster cleaning cycle"],
      ["2", "patents filed"],
    ],
    tags: ["Computer vision", "6D pose", "Grasp planning", "Mechanical design", "Firmware"],
    body: [
      "I co-founded Dishcare to build an autonomous robotic dishwasher aimed at reinventing how dishes get done. Our custom-built robot uses a specialized gripper and computer vision — 6D pose estimation and dirt detection — to pick up each dish robustly and clean it in a closed loop.",
      "That closed loop is the whole trick: clean only where it's dirty, stop when it's clean. The result was a 50% reduction in energy and water versus top-of-the-line dishwashers. I designed, built, and programmed the alpha prototype.",
    ],
    media: [
      { kind: "youtube", id: "NJi4vIFSSP8", poster: "/dishcare_alpha.jpg", caption: "Ready for public demos", date: "Jan 2023" },
      { kind: "youtube", id: "M0u1OEaR8qg", poster: "/grasp_plan.jpg", caption: "Optimal grasp planning", date: "Nov 2022" },
      { kind: "youtube", id: "gKb8Jq5TVWQ", poster: "/build_speed.jpg", caption: "Cleaning 80% faster", date: "Oct 2022" },
      { kind: "youtube", id: "nKG6b9ucRus", poster: "/fallen_grasp.jpg", caption: "Compact gripper", date: "Aug 2022" },
      { kind: "youtube", id: "Sof6cUwv11Y", poster: "/conserve_demo.jpg", caption: "Water & energy conservation", date: "Jun 2022" },
      { kind: "youtube", id: "2KN2tbj6Ga0", poster: "/dirt_target.jpg", caption: "Dirt targeting", date: "May 2022" },
      { kind: "youtube", id: "-b4YNequIok", poster: "/custom_robot.jpg", caption: "First custom robot", date: "Feb 2022" },
      { kind: "youtube", id: "cVVyoYjPejQ", poster: "/proof_concept.jpg", caption: "Proof of concept", date: "Jan 2022" },
      { kind: "youtube", id: "7RiLpxEcmdo", poster: "/dishcare_ani.jpg", caption: "The envisioned design", date: "Dec 2021" },
    ],
    pubs: [
      {
        title: "Patent: Autonomous Dishwasher",
        authors: "Pratik Chawla, Sri Rama Prasanna Pavani",
        href: "https://drive.google.com/file/d/1847yK53aH91Tu4bxY-J3tcwbpWmwK8_v/view?usp=drive_link",
      },
      {
        title: "Patent: Rolling Grasp End Effector (pending)",
        authors: "Pratik Chawla, Sri Rama Prasanna Pavani",
        href: "https://drive.google.com/file/d/13-kOHSef8FwIoO-01wwtASFe8BQP05gB/view?usp=drive_link",
      },
      {
        title: "Robotic End Effector for Autonomous Cleaning of Dishes in Homes and Offices",
        authors: "Sri Rama Prasanna Pavani, Pratik Chawla, Anurag Agrawal",
        venue: "Dishcare, 2022",
        href: "https://drive.google.com/file/d/1JPHGbYTht8I6DwKoNhjotcAXyMtDi3rA/view?usp=sharing",
      },
      {
        title: "Minimizing Pathogens on Everyday Dishes With an Autonomous Dishwasher",
        authors: "Sri Rama Prasanna Pavani, Shrutidhara Kakoti, Pratik Chawla",
        venue: "Dishcare, 2022",
        href: "https://drive.google.com/file/d/1GtSgzthkYT8uhbntksFMPN8L6gUEvKmd/view?usp=share_link",
      },
    ],
  },
  {
    slug: "plain2fun",
    name: "Plain2Fun",
    color: BLUE,
    role: "M.S. research · Purdue C Design Lab",
    when: "2018",
    kicker: "Printing circuits onto everyday objects.",
    summary:
      "A custom four-axis machine that scans bottles, vases and mugs and draws working circuits onto them — turning plain objects into alarm clocks and status indicators.",
    cover: "/plain2fun.jpg",
    hover: "/PLAIN2FUN.gif",
    link: {
      label: "Paper (PDF)",
      href: "https://www.researchgate.net/profile/Ke-Huo/publication/325707812_Plain2Fun_Augmenting_Ordinary_Objects_with_Interactive_Functions_by_Auto-Fabricating_Surface_Painted_Circuits/links/5b2c5e35aca2720785d660bf/Plain2Fun-Augmenting-Ordinary-Objects-with-Interactive-Functions-by-Auto-Fabricating-Surface-Painted-Circuits.pdf",
    },
    stats: [
      ["4-axis", "custom machine"],
      ["CHI · DIS", "published 2018"],
    ],
    tags: ["Machine design", "Firmware", "HCI", "Fabrication"],
    body: [
      "For my Master's at Purdue's C Design Lab, under Dr. Karthik Ramani, I worked on Plain2Fun — a workflow that turns everyday objects like bottles, vases, and mugs into functional things: alarm clocks, status indicators, temperature indicators.",
      "I designed and built the four-axis machine that holds, rotates, and scans near-cylindrical objects while drawing circuits on them from an easy-to-use GUI. I also wrote the firmware for the microcontrollers users attach to their objects, and designed the interface that connects rigid electronics to the drawn circuits — the part that actually brings the objects to life.",
    ],
    media: [
      { kind: "youtube", id: "mIlBj9MfFP8", poster: "/plain2fun.jpg", caption: "The 4-axis circuit printing machine" },
      { kind: "image", src: "/status_pointer.gif", poster: "/status.jpg", caption: "Water bottle → status indicator" },
      { kind: "image", src: "/alarm_clock.gif", poster: "/chlorox.jpg", caption: "Clorox container → alarm clock" },
    ],
    pubs: [
      {
        title:
          "Plain2Fun: Augmenting Ordinary Objects with Interactive Functions by Auto-Fabricating Surface Painted Circuits",
        authors: "Tianyi Wang, Ke Huo, Pratik Chawla, Guiming Chen, Siddharth Banerjee, Karthik Ramani",
        venue: "DIS 2018 — Designing Interactive Systems Conference",
        href: "https://dl.acm.org/doi/abs/10.1145/3196709.3196791",
      },
      {
        title: "Plain2Fun: Augmenting Ordinary Objects with Surface Painted Circuits",
        authors: "Tianyi Wang, Ke Huo, Pratik Chawla, Guiming Chen, Siddharth Banerjee, Karthik Ramani",
        venue: "CHI 2018 Extended Abstracts, pp. 1–6",
        href: "https://dl.acm.org/doi/abs/10.1145/3170427.3188655",
      },
      {
        title: "Overprint — project update",
        authors: "Pratik Chawla",
        href: "https://drive.google.com/file/d/1Sj1F9A409TKgc-gG2niMjEWBRlVs8eLK/view?usp=sharing",
      },
    ],
  },
  {
    slug: "promaxo",
    name: "Promaxo",
    color: GOLD,
    role: "Advanced Hardware Engineer",
    kicker: "The electronics rack for an in-office MRI.",
    summary:
      "Designed, built, and tested the complete electronics rack for an in-office MRI machine — high-power and low-power systems, 2000+ components under PDM control.",
    cover: "/promaxo.png",
    contain: true,
    link: { label: "promaxo.com", href: "https://www.promaxo.com/" },
    stats: [
      ["2000+", "components under PDM"],
      ["2", "interns managed"],
    ],
    tags: ["Electrical systems", "PDM", "Mechanical design", "Test"],
    body: [
      "At Promaxo I owned the design, build, and test of the entire electronics rack for an in-office MRI machine. That meant maintaining the full hardware package — 3D models, 2D drawings, and over 2000 components managed through Product Data Management tools.",
      "It was hands-on, too: building and testing high-power and low-power electrical systems to guarantee performance and reliability. Along the way I managed two interns and worked across scientists, contract manufacturers, and engineers to get it out the door.",
    ],
    media: [{ kind: "image", src: "/promaxo.png", caption: "Advanced Hardware Engineer", contain: true }],
    pubs: [],
  },
  {
    slug: "skyven",
    name: "Skyven",
    color: HEAT,
    role: "Product Development Engineer",
    kicker: "Solar heat for industry.",
    summary:
      "Built and evaluated solar heating receivers to 65% system-wide efficiency, ran reliability testing, and automated the shop floor.",
    cover: "/skyven.jpg",
    link: { label: "skyven.co", href: "https://www.skyven.co/" },
    stats: [
      ["65%", "system-wide efficiency"],
      ["50", "systems delivered to Cornell"],
    ],
    tags: ["Thermal", "Reliability testing", "Automation"],
    body: [
      "As a Product Development Engineer at Skyven Technologies I built and evaluated solar heating receivers, ultimately reaching a system-wide efficiency of 65%.",
      "I ran the reliability program — accelerated heat, vibration, and pressure testing — and worked on process and machine automation: a sun tracker, a slip roll, and automatic sticker removal, all to streamline operations and raise throughput.",
    ],
    media: [{ kind: "image", src: "/skyven.jpg", caption: "Product Development Engineer" }],
    pubs: [
      {
        title: "Skyven Delivers 50 Solar Heating Systems to Cornell University",
        venue: "Press, April 2021",
        href: "https://www.ststartup.com/2021/04/08/startup-installs-innovative-solar-tech-at-cornell/",
      },
    ],
  },
  {
    slug: "dfam",
    name: "DfAM",
    color: VIOLET,
    role: "Teaching Assistant · Research",
    when: "2017",
    kicker: "A worksheet that makes 3D printing less painful.",
    summary:
      "Validated the Design for Additive Manufacturing worksheet with 40+ students — work that won the ASME JMD 2017 Editors' Choice award.",
    cover: "/dfam.PNG",
    contain: true,
    link: {
      label: "The worksheet",
      href: "https://drive.google.com/file/d/11B57f-mjWCJKKYfAxFe9CPG2qNxqAG39/view?usp=share_link",
    },
    stats: [
      ["40+", "students studied"],
      ["Editors' Choice", "ASME JMD 2017"],
    ],
    tags: ["Design research", "Additive manufacturing", "Teaching"],
    body: [
      "The Design for Additive Manufacturing (DfAM) worksheet helps novices pick and finalize designs for 3D printing. Its scoring system is used before anything goes to the printer, steering people around the classic additive-manufacturing mistakes.",
      "As a TA for a CAD and rapid-prototyping class, I tested it for real: I built the assignments around the students' final project — design and build a toy — introduced the worksheet, and collected and analyzed data from 40+ students. The analysis showed the worksheet works, and supported our hypothesis.",
    ],
    media: [{ kind: "image", src: "/dfam.PNG", caption: "The DfAM worksheet", contain: true }],
    pubs: [
      {
        title: "2017 Editors' Choice Paper Award: The Design for Additive Manufacturing Worksheet",
        authors: "Joran W. Booth, Jeffrey Alperovich, Pratik Chawla, Jiayan Ma, Tahira N. Reid, Karthik Ramani",
        venue: "ASME JMD 2017",
        href: "https://asmejmd.org/2018/10/03/announcing-the-2017-editors-choice-award-and-honorable-mentions-2/",
      },
      {
        title: "The Design for Additive Manufacturing Worksheet",
        authors: "Joran W. Booth, Jeffrey Alperovich, Pratik Chawla, Jiayan Ma, Tahira N. Reid, Karthik Ramani",
        venue: "ASME J. Mech. Des., October 2017",
        href: "https://asmedigitalcollection.asme.org/mechanicaldesign/article/139/10/100904/366998/The-Design-for-Additive-Manufacturing-Worksheet",
      },
    ],
  },
  {
    slug: "maha",
    name: "MAHA Fluid Power",
    color: MINT,
    role: "Research Assistant",
    when: "2015",
    kicker: "Instrumenting a variable-displacement vane pump.",
    summary:
      "Built a test rig to validate a vane-pump model, fusing six sensor streams to measure fluid pressure at every rotation.",
    cover: "/vdvp.jpg",
    link: { label: "SURF 2015", href: "https://docs.lib.purdue.edu/surf/2015/presentations/120/" },
    stats: [["6", "sensor streams fused"]],
    tags: ["Test rigs", "Sensors", "Data analysis"],
    body: [
      "At Purdue's MAHA Fluid Power Lab I built a test rig to validate the model of a Variable Displacement Vane Pump — a piece of hardware with a lot of moving parts that needed careful analysis before anyone could build a robust control system for it.",
      "I selected and installed the sensors — pressure transducers and an LVDT — to collect data across flow rates and displacements, proposed hardware modifications for the sensor integration, and wrote the algorithm that compiled and visualized six sensor streams to measure fluid pressure at every rotation of the pump.",
    ],
    media: [
      { kind: "image", src: "/vdvp.jpg", caption: "Pump sensor placements" },
      { kind: "image", src: "/VDVP.PNG", caption: "Sensor-fusion visualization" },
    ],
    pubs: [
      {
        title: "Test Rig Design for Variable Displacement Vane Pump",
        authors: "Pratik Chawla, Ryan Jenkins, Monika Ivantysynova",
        venue: "SURF Symposium, Purdue University, 2015",
        href: "https://drive.google.com/file/d/1vhQFKcXQV2YW7oIyRQ68_72XdYnN4n5Q/view?usp=sharing",
      },
    ],
  },
];

// side projects that live elsewhere on the internet
export const EXTERNAL = [
  {
    name: "logtennis.net",
    color: ACCENT,
    role: "Maker · live",
    kicker: "A social tennis tracker I built and use every week.",
    summary:
      "Attested match logging, a Spiderweb of Rivalry, nemesis detection, and one (1) public Excuse Dropdown. Every match on my tennis page comes from it.",
    cover: "/logtennis.png",
    href: "https://logtennis.net",
  },
];

const LEGACY = { dishcare: "dishcare", plain2fun: "plain2fun", skyven: "skyven", promaxo: "promaxo", dfam: "dfam", maha: "maha" };

export const slugFor = (name) => LEGACY[String(name).toLowerCase()] || null;
export const projectBySlug = (slug) => PROJECTS.find((p) => p.slug === slug);
